import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import useI18n from '../../../i18n/useI18n'
import { Actions as ApiCallActions } from '../../../redux/apiCall/reducers'
import RoleBasedAccess from '../../../components/role-based-access'
import { UserTypeEnum } from '../../../utils/enums'
import CommissionCard from './CommissionCard'
import CommissionsFilter, { commissionFilterDefaults } from './CommissionsFilter'
import Pagination from '../../../components/pagination'
import './commissions.scss'

const AgentCommissions = () => {
    const dispatch = useDispatch()
    const { t } = useI18n()
    const [agents, setAgents] = useState([])
    const [loading, setLoading] = useState(true)
    const [filters, setFilters] = useState(commissionFilterDefaults)
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
    })

    useEffect(() => {
        fetchAgents(1, filters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const buildQueryParams = (page, filterParams, limitOverride = null) => {
        const queryParams = {
            page,
            limit: limitOverride || pagination.limit
        }

        const addQueryParam = (key, value) => {
            if (value === undefined || value === null) {
                return
            }
            if (typeof value === 'string') {
                const trimmed = value.trim()
                if (!trimmed) {
                    return
                }
                queryParams[key] = trimmed
                return
            }
            queryParams[key] = value
        }

        addQueryParam('search', filterParams.search)
        addQueryParam('city', filterParams.city)
        addQueryParam('country', filterParams.country)
        addQueryParam('has_active_contract', filterParams.has_active_contract)
        addQueryParam('contract_expiring_soon', filterParams.contract_expiring_soon)
        addQueryParam('sort', filterParams.sort)
        addQueryParam('order', filterParams.order)

        return queryParams
    }

    const fetchAgents = (page = 1, filterParams = filters, limitOverride = null) => {
        setLoading(true)
        dispatch(
            ApiCallActions.Get({
                url: 'agents/commission-management',
                query: buildQueryParams(page, filterParams, limitOverride),
                onSuccess: ({ data }) => {
                    const agentsData = data.data || data || []
                    setAgents(Array.isArray(agentsData) ? agentsData : [])
                    setPagination({
                        total: data.meta?.total || 0,
                        page: data.meta?.page || 1,
                        limit: data.meta?.limit || pagination.limit,
                        totalPages: data.meta?.totalPages || 1
                    })
                    setLoading(false)
                },
                onError: () => {
                    setAgents([])
                    setLoading(false)
                }
            })
        )
    }

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters)
    }

    const handleFilterSubmit = (newFilters) => {
        setFilters(newFilters)
        fetchAgents(1, newFilters)
    }

    const handleClearFilters = (clearedFilters) => {
        setFilters(clearedFilters)
        fetchAgents(1, clearedFilters)
    }

    const handlePageChange = (page) => {
        fetchAgents(page, filters)
    }

    const handleLimitChange = (newLimit) => {
        const updatedPagination = {
            ...pagination,
            limit: newLimit,
            page: 1
        }
        setPagination(updatedPagination)
        fetchAgents(1, filters, newLimit)
    }

    return (
        <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
            <section className="content">
                <div className="block-header">
                    <div className="row">
                        <div className="col-lg-7 col-md-6 col-sm-12 text-left">
                            <h2>
                                {t('AGENT_COMMISSIONS_TITLE')}
                                <small>{t('AGENT_COMMISSIONS_SUBTITLE')}</small>
                            </h2>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-12">
                            <ul className="breadcrumb float-md-right">
                                <li className="breadcrumb-item">
                                    <a href="/dashboard">
                                        <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                                    </a>
                                </li>
                                <li className="breadcrumb-item active">{t('AGENT_COMMISSIONS_TITLE')}</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="container-fluid">
                    {/* Filter Card */}
                    <div className="row clearfix">
                        <div className="col-lg-12">
                            <CommissionsFilter
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                onFilterSubmit={handleFilterSubmit}
                                onClearFilters={handleClearFilters}
                                onRefresh={() => fetchAgents(1, filters)}
                                loading={loading}
                            />
                        </div>
                    </div>

                    {/* Commission Cards Grid */}
                    {loading ? (
                        <div className="row clearfix">
                            <div className="col-lg-12 text-center">
                                <div className="card">
                                    <div className="body">
                                        <i className="zmdi zmdi-spinner zmdi-hc-spin" style={{ fontSize: '48px', color: '#f96332' }}></i>
                                        <p className="mt-3">{t('COMMON_LOADING')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : agents.length === 0 ? (
                        <div className="row clearfix">
                            <div className="col-lg-12 text-center">
                                <div className="card">
                                    <div className="body">
                                        <i className="zmdi zmdi-info-outline" style={{ fontSize: '48px', color: '#999' }}></i>
                                        <p className="mt-3">
                                            {filters.search ? t('AGENT_COMMISSIONS_NO_RESULTS') : t('AGENT_COMMISSIONS_EMPTY')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="row clearfix">
                            {agents.map(agent => (
                                <div key={agent.id || agent?.agent?.id} className="col-lg-6 col-md-12">
                                    <CommissionCard
                                        agent={agent}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && agents.length > 0 && (
                        <div className="row clearfix">
                            <div className="col-lg-12">
                                <Pagination
                                    currentPage={pagination.page}
                                    totalPages={pagination.totalPages}
                                    total={pagination.total}
                                    limit={pagination.limit}
                                    onPageChange={handlePageChange}
                                    onLimitChange={handleLimitChange}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </RoleBasedAccess>
    )
}

export default AgentCommissions
